import { Button, Group, Menu, Stack, Table, Text } from '@common/ui'
import { IconChevronDown } from '@common/ui/icons'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { com } from '@eidu/web'
import AddExistingToSetModal from './AddExistingToSetModal'
import CreateEntityInSetModal from './CreateEntityInSetModal'
import EntityLabelLines from '../data/EntityLabelLines'
import EqualityHashMap from '../../util/EqualityHashMap'
import useEntityById from '../../io/useEntityById'
import useEntityTypes, { EMPTY_ENTITY_TYPES } from '../../io/useEntityTypes'
import usePaginatedEntitiesOfType from '../../io/usePaginatedEntitiesOfType'
import { useModifyEntity } from '../../io/useEntityMutations'
import { getKindPageConfig } from '../../pages/entity/kindViewRegistry'
import DefaultEditPage from '../../pages/entity/kinds/default/DefaultEditPage'
import Field = com.eidu.entity.field.Field
import EntityId = com.eidu.entity.EntityId
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import ReferenceSetValue = com.eidu.entity.field.ReferenceSetValue

export type ReferenceSetEditorProps = {
  entityId: EntityId
  field: Field
  editMode?: boolean
}

const ReferenceSetEditor = ({ entityId, field, editMode = false }: ReferenceSetEditorProps) => {
  const navigate = useNavigate()
  const { entity } = useEntityById(entityId)
  const modifyMutation = useModifyEntity()

  const [adding, setAdding] = useState(false)
  const [creating, setCreating] = useState(false)

  const { entityTypes } = useEntityTypes()
  const allTypes = entityTypes ?? EMPTY_ENTITY_TYPES

  const validReferenceTypeIds: EntityTypeId[] = Array.from(field.validReferenceTypes?.asJsReadonlySetView() ?? [])
  const firstTypeId = validReferenceTypeIds[0]

  const {
    entities: candidates,
    loadMore,
    hasMore,
    loading,
  } = usePaginatedEntitiesOfType({
    typeId: firstTypeId ?? allTypes.keys().next().value,
    allTypes,
  })

  const candidatesById = new EqualityHashMap(candidates.map((it) => [it.entity.id, it]))

  const members: EntityId[] = (() => {
    const value = entity
      ? Array.from(entity.entity.valuesByFieldId.asJsReadonlyMapView().entries()).find(([id]) =>
          id.equals(field.id)
        )?.[1]
      : undefined
    return value instanceof ReferenceSetValue ? Array.from(value.value.asJsReadonlySetView()) : []
  })()

  const patchMember = (item: EntityId, present: boolean) =>
    modifyMutation.mutateAsync({
      id: entityId,
      patch: {
        valuesToUpdateByFieldId: new Map(),
        referenceSetItemsToUpdateByFieldId: new Map([[field.id, new Map([[item, present]])]]),
        linksToUpdate: new Map(),
        externalValuesToUpdate: new Map(),
      },
    })

  const targetType = firstTypeId ? allTypes.get(firstTypeId) : undefined
  const canCreateInline = targetType !== undefined && getKindPageConfig(targetType.kind).EditPage === DefaultEditPage

  const columnLabel = targetType?.name ?? field.name

  const memberIds = new Set(members.map((it) => it.toString()))
  const parentLabel = entity?.primaryLabelText
  const addTitle = parentLabel ? `Add ${columnLabel} to ${parentLabel}` : `Add ${columnLabel}`

  return (
    <Stack gap="xs">
      {editMode && (
        <Group justify="flex-end" align="center" wrap="nowrap">
          {canCreateInline && targetType ? (
            <Menu position="bottom-end">
              <Menu.Target>
                <Button
                  size="xs"
                  rightSection={<IconChevronDown size={14} stroke={1.5} />}
                  data-testid="reference-set-add-button"
                >
                  Add
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  onClick={() => {
                    setCreating(false)
                    setAdding(true)
                  }}
                >
                  Add existing…
                </Menu.Item>
                <Menu.Item
                  onClick={() => {
                    setAdding(false)
                    setCreating(true)
                  }}
                >
                  Create new…
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : (
            <Button size="xs" onClick={() => setAdding(true)} data-testid="reference-set-add-button">
              Add
            </Button>
          )}
        </Group>
      )}

      <div
        style={{
          border: '1px solid #eee',
          borderRadius: 'var(--trellis-radius-md)',
          maxHeight: 320,
          overflowY: 'auto',
        }}
      >
        {members.length === 0 ? (
          <Text c="dimmed" px="sm" py="md">
            This set is empty
          </Text>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Tbody>
              {members.map((member) => {
                const resolved = candidatesById.get(member)
                const typeId = resolved?.entity?.typeId ?? firstTypeId
                return (
                  <Table.Tr
                    key={member.toString()}
                    data-testid="reference-set-member"
                    style={{ cursor: typeId ? 'pointer' : 'default' }}
                    onClick={() => typeId && navigate(`/entities/${typeId.toString()}/view/${member.toString()}`)}
                  >
                    <Table.Td>
                      <EntityLabelLines
                        primary={resolved?.primaryLabelText ?? member.toString()}
                        secondary={resolved?.secondaryLabelText}
                      />
                    </Table.Td>
                    {editMode && (
                      <Table.Td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="subtle"
                          size="xs"
                          color="gray"
                          onClick={() => patchMember(member, false)}
                          disabled={modifyMutation.isPending}
                        >
                          Remove
                        </Button>
                      </Table.Td>
                    )}
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        )}
      </div>

      <AddExistingToSetModal
        opened={adding}
        onClose={() => setAdding(false)}
        title={addTitle}
        candidates={candidates}
        isMember={(id) => memberIds.has(id.toString())}
        onAdd={(id) => patchMember(id, true)}
        busy={modifyMutation.isPending}
        loading={loading}
        hasMore={hasMore}
        loadMore={loadMore}
      />

      {targetType && (
        <CreateEntityInSetModal
          opened={creating}
          onClose={() => setCreating(false)}
          entityType={targetType}
          entityTypes={allTypes}
          setName={field.name}
          onCreated={async (id) => {
            await patchMember(id, true)
          }}
        />
      )}
    </Stack>
  )
}

export default ReferenceSetEditor
